import type { Monitor } from '@/types/api';

/**
 * The figures that say whether anything being monitored is wrong, worked out once.
 *
 * The Monitoring page and the dashboard both show them. Two copies of "what counts as down" would
 * drift the first time either changed, and a dashboard reporting one monitor down while the page it
 * links to reports none is worse than showing neither.
 *
 * ## Stale is its own count, never folded into up or down
 *
 * A monitor whose last reading is too old is not up — nobody knows what it is. Counting it as up is
 * the one failure that makes monitoring worse than having none, so it is counted separately, and a
 * stale monitor is excluded from up and down whatever its stored status says.
 *
 * ## Expiry monitors have no uptime
 *
 * An SSL or domain-expiry monitor answers "how long is left", checked once a day. A percentage of
 * those checks describes nothing a visitor experiences, so they are left out of the average rather
 * than letting a certificate with ten days left drag a site's uptime down.
 */
export interface MonitorSummary {
  up: number;
  down: number;
  /** Switched on, but the last reading is too old to present as current. */
  stale: number;
  paused: number;
  /** Mean 24-hour uptime across the monitors with enough checks to count; null when none have. */
  averageUptime: number | null;
}

/** Whether this kind fails on a countdown rather than on a request. */
export const isExpiryKind = (kind: string) => kind === 'ssl' || kind === 'domain_expiry';

export function summarizeMonitors(monitors: Monitor[]): MonitorSummary {
  const live = monitors.filter((monitor) => monitor.status !== 'paused');

  // The API sends null below its own minimum sample count, so a present figure is one that counts.
  const measured = live
    .filter((monitor) => !isExpiryKind(monitor.kind))
    .map((monitor) => monitor.uptime_24h)
    .filter((uptime): uptime is number => uptime !== null && uptime !== undefined);

  return {
    up: live.filter((monitor) => !monitor.is_stale && monitor.status === 'up').length,
    down: live.filter((monitor) => !monitor.is_stale && monitor.status === 'down').length,
    stale: live.filter((monitor) => monitor.is_stale).length,
    paused: monitors.length - live.length,
    averageUptime: measured.length === 0 ? null : measured.reduce((total, uptime) => total + uptime, 0) / measured.length,
  };
}

/**
 * An uptime percentage for display.
 *
 * Two decimals, because the difference between 99.9% and 99.99% is about forty minutes a month; but a
 * perfect record is written "100%", not "100.00%", which reads as a measuring instrument rather than
 * as good news, and nothing at all is "0%".
 */
export function formatUptimePercent(uptime: number): string {
  if (uptime >= 100) return '100%';
  if (uptime <= 0) return '0%';

  return `${uptime.toFixed(2)}%`;
}

/**
 * How long an expiry monitor has left, worded so the number carries its own urgency.
 *
 * Null until the first check completes, and after a check that could not read a date — in which case
 * saying so is right, because the platform genuinely does not know.
 */
export function expiryLabel(days: number | null): string {
  if (days === null) {
    return 'No expiry date read yet';
  }

  if (days < 0) {
    const ago = Math.abs(days);

    return `Expired ${ago} day${ago === 1 ? '' : 's'} ago`;
  }

  if (days === 0) {
    return 'Expires today';
  }

  return `${days} day${days === 1 ? '' : 's'} remaining`;
}

/**
 * Most urgent first: down, then not checked recently, then waiting for a first check, then up, with
 * paused last.
 *
 * Stale ranks above healthy on purpose. A reading the platform has not refreshed is no news, not
 * good news, and burying it under monitors that are genuinely fine is how a stopped sweep goes
 * unnoticed.
 */
export function monitorUrgency(monitor: Monitor): number {
  if (monitor.status === 'paused') return 4;
  if (monitor.is_stale) return 1;
  if (monitor.status === 'down') return 0;
  if (monitor.status === 'pending') return 2;

  return 3;
}
